import React, { useState, useEffect, useMemo } from 'react';
import { Grid, Typography, Box, CircularProgress, Card, CardContent, Tooltip as MuiTooltip } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import ScoreboardIcon from '@mui/icons-material/Scoreboard';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import InfoIcon from '@mui/icons-material/Info';
import SizeAnalysis from '../components/Dashboard/SizeAnalysis';
import { SizeBucketMeta } from '../components/Dashboard/SizeAnalysis';
import { useCountry } from '../context/CountryContext';
import { API_BASE_URL } from '../config';
import { COUNTRY_META } from '../utils/countries';
import { RankedCompanyApi } from '../types/marketOverview';

interface MarketOverviewSummaryApi {
    summary_stats?: {
        total_companies?: number;
        avg_cents_on_dollar?: number | null;
        improving_percent?: number;
        worsening_percent?: number;
        stable_percent?: number;
        avg_composite_score?: number;
    };
}

const ROWS_PER_PAGE = 10;

const fetchSummary = async (country: string): Promise<MarketOverviewSummaryApi> => {
    const url = `${API_BASE_URL}/market-overview?country=${encodeURIComponent(country)}&view=summary`;
    const result = await fetch(url);
    if (!result.ok) {
        throw new Error(`Failed to fetch market summary (${result.status})`);
    }
    return result.json();
};

const fetchSizeBuckets = async (country: string): Promise<SizeBucketMeta[]> => {
    const url = `${API_BASE_URL}/market-overview?country=${encodeURIComponent(country)}&view=size_buckets`;
    const result = await fetch(url);
    if (!result.ok) {
        throw new Error(`Failed to fetch size buckets (${result.status})`);
    }
    const payload = await result.json();
    return Array.isArray(payload?.size_buckets) ? payload.size_buckets : [];
};

const fetchCompaniesPage = async (
    country: string,
    sizeBucketId: string,
    page: number,
    pageSize: number
): Promise<{ items: RankedCompanyApi[]; total_count: number }> => {
    const url = `${API_BASE_URL}/market-overview?country=${encodeURIComponent(country)}&view=companies&size_bucket=${encodeURIComponent(sizeBucketId)}&page=${page}&page_size=${pageSize}`;
    const result = await fetch(url);
    if (!result.ok) {
        throw new Error(`Failed to fetch companies (${result.status})`);
    }
    const payload = await result.json();
    return {
        items: Array.isArray(payload?.items) ? payload.items : [],
        total_count: Number(payload?.total_count || 0),
    };
};

const SummaryCard: React.FC<{ title: string; value: string; icon: React.ReactElement; color: string; tooltip?: string }> = ({ title, value, icon, color, tooltip }) => (
    <Card elevation={3} sx={{ borderRadius: 3, p: 3, height: 180, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Typography variant="subtitle1" color="text.secondary">{title}</Typography>
            <Box sx={{ color: color }}>
                {React.cloneElement(icon, { style: { fontSize: 32 } })}
            </Box>
        </Box>
        <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{value}</Typography>
            {tooltip ? (
                <MuiTooltip title={tooltip} arrow>
                    <InfoIcon fontSize="small" color="action" sx={{ cursor: 'help', fontSize: 16 }} />
                </MuiTooltip>
            ) : null}
        </Box>
    </Card>
);

const MarketOverview: React.FC = () => {
    const { country } = useCountry();

    const [summaryData, setSummaryData] = useState<MarketOverviewSummaryApi | null>(null);
    const [sizeBuckets, setSizeBuckets] = useState<SizeBucketMeta[]>([]);
    const [selectedSizeBucketId, setSelectedSizeBucketId] = useState<string>('');

    const [rankedCompanies, setRankedCompanies] = useState<RankedCompanyApi[]>([]);
    const [rankedCompaniesTotalCount, setRankedCompaniesTotalCount] = useState<number>(0);
    const [rankedCompaniesPage, setRankedCompaniesPage] = useState<number>(0);

    const [loading, setLoading] = useState<boolean>(true);
    const [loadingCompanies, setLoadingCompanies] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const loadOverview = async () => {
            setLoading(true);
            setError(null);
            try {
                const [summary, buckets] = await Promise.all([
                    fetchSummary(country),
                    fetchSizeBuckets(country),
                ]);
                setSummaryData(summary);
                setSizeBuckets(buckets);

                const firstBucket = buckets.find((bucket) => bucket.id === '20000_or_more') || buckets[0] || null;
                setSelectedSizeBucketId(firstBucket?.id || '');
                setRankedCompaniesPage(0);
            } catch (err: any) {
                setError(err.message || 'Failed to fetch data from backend.');
            } finally {
                setLoading(false);
            }
        };

        loadOverview();
    }, [country]);

    useEffect(() => {
        if (!selectedSizeBucketId) {
            setRankedCompanies([]);
            setRankedCompaniesTotalCount(0);
            return;
        }

        const loadCompanies = async () => {
            setLoadingCompanies(true);
            setError(null);
            try {
                const payload = await fetchCompaniesPage(country, selectedSizeBucketId, rankedCompaniesPage, ROWS_PER_PAGE);
                setRankedCompanies(payload.items);
                setRankedCompaniesTotalCount(payload.total_count);
            } catch (err: any) {
                setError(err.message || 'Failed to fetch ranked companies.');
            } finally {
                setLoadingCompanies(false);
            }
        };

        loadCompanies();
    }, [country, selectedSizeBucketId, rankedCompaniesPage]);

    const summaryStats = summaryData?.summary_stats || {};
    const totalCompanies = summaryStats.total_companies || 0;
    const rawAvgCents = summaryStats.avg_cents_on_dollar;
    const avgCentsOnDollar = rawAvgCents !== null && rawAvgCents !== undefined ? rawAvgCents.toFixed(2) : 'N/A';
    const improvingPercent = (summaryStats.improving_percent || 0).toFixed(0);
    const worseningPercent = (summaryStats.worsening_percent || 0).toFixed(0);
    const stablePercent = (summaryStats.stable_percent || 0).toFixed(0);
    const avgCompositeScore = (summaryStats.avg_composite_score || 0).toFixed(1);

    const countryMeta = useMemo(() => COUNTRY_META[country], [country]);
    const countryName = countryMeta.label;
    const currencyUnit = countryMeta.currencyUnit;
    const currencyBase = countryMeta.currencyBase;

    const marketSummaryData = useMemo(
        () => [
            {
                title: 'Total Companies',
                value: totalCompanies.toLocaleString(),
                icon: <BusinessIcon />,
                color: '#1976d2',
                tooltip: 'Total number of companies included in this dataset.'
            },
            ...(rawAvgCents !== null && rawAvgCents !== undefined ? [{
                title: `Avg. ${currencyUnit === 'pence' ? 'Pence' : 'Cents'} on the ${currencyBase === 'pound' ? 'Pound' : 'Euro'}`,
                value: `${avgCentsOnDollar}`,
                icon: <MonetizationOnIcon />,
                color: '#6a1b9a',
                tooltip: `In ${countryName} in 2024, women earn ${avgCentsOnDollar} ${currencyUnit} for every ${currencyBase} a man earns.`
            }] : []),
            {
                title: 'Improving',
                value: `${improvingPercent}%`,
                icon: <TrendingUpIcon />,
                color: '#388e3c',
                tooltip: 'Percentage of companies that have narrowed their gender pay gap compared to the previous year.'
            },
            {
                title: 'Worsening',
                value: `${worseningPercent}%`,
                icon: <TrendingDownIcon />,
                color: '#d32f2f',
                tooltip: 'Percentage of companies where the gender pay gap has widened compared to the previous year.'
            },
            {
                title: 'Stable / Unavailable',
                value: `${stablePercent}%`,
                icon: <TrendingFlatIcon />,
                color: '#757575',
                tooltip: 'Percentage of companies where the gender pay gap has remained unchanged or insufficient data is available.'
            },
            {
                title: 'Average Score',
                value: avgCompositeScore,
                icon: <ScoreboardIcon />,
                color: '#f57c00',
                tooltip: 'The average composite score across all companies, factoring in mean/median pay gaps and bonus gaps.'
            },
        ],
        [
            totalCompanies,
            rawAvgCents,
            currencyUnit,
            currencyBase,
            avgCentsOnDollar,
            countryName,
            improvingPercent,
            worseningPercent,
            stablePercent,
            avgCompositeScore,
        ]
    );

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
    if (error) return <Typography color="error" align="center" sx={{ mt: 4 }}>{error}</Typography>;

    return (
        <Box sx={{ p: 4, bgcolor: 'background.default' }}>
            <Grid container spacing={4}>
                <Grid item xs={12}>
                    <Box sx={{ mb: 5 }}>
                        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                            Market Overview
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
                            Viewing most recent data for: {countryName}
                        </Typography>
                    </Box>
                    <Grid container spacing={4}>
                        {marketSummaryData.map((item, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <SummaryCard {...item} />
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Card elevation={3} sx={{ borderRadius: 3, p: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>Company Rankings</Typography>
                            <SizeAnalysis
                                rankedCompanies={rankedCompanies}
                                totalCount={rankedCompaniesTotalCount}
                                loading={loadingCompanies}
                                sizeBuckets={sizeBuckets}
                                selectedSizeBucketId={selectedSizeBucketId}
                                onSelectSizeBucket={(id) => {
                                    setSelectedSizeBucketId(id);
                                    setRankedCompaniesPage(0);
                                }}
                                page={rankedCompaniesPage}
                                rowsPerPage={ROWS_PER_PAGE}
                                onPageChange={setRankedCompaniesPage}
                            />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box >
    );
};

export default MarketOverview;
